import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpsertStickerPack, type MediaStickerPack } from '../api';

// Mirrors MediaValidation.PACK_ID on the backend, leading digit included —
// live packs are named "3d_emoji" and "y2k_chrome".
const packSchema = z.object({
  packId: z
    .string()
    .regex(
      /^[a-z0-9][a-z0-9_]{1,63}$/,
      '2-64 characters: lowercase letters, digits and underscores',
    ),
  displayName: z.string().trim().min(1, 'Required').max(128),
  sortOrder: z.coerce.number().int().min(0).max(999).optional(),
});

type PackForm = z.input<typeof packSchema>;

/**
 * Create a pack or edit its label / tab position.
 *
 * <p>The pack id is fixed after creation: it is the S3 folder every sticker in
 * the pack is stored under and the {@code categoryId} the mobile catalog
 * carries, so changing it would orphan both.
 */
export function StickerPackDialog({
  pack,
  open,
  onClose,
}: {
  pack: MediaStickerPack | null;
  open: boolean;
  onClose: () => void;
}) {
  const upsert = useUpsertStickerPack();
  const isEdit = pack !== null;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PackForm>({ resolver: zodResolver(packSchema) });

  useEffect(() => {
    if (!open) return;
    reset({
      packId: pack?.packId ?? '',
      displayName: pack?.displayName ?? '',
      sortOrder: pack?.sortOrder,
    });
    upsert.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pack]);

  const onSubmit = handleSubmit(async (values) => {
    const parsed = packSchema.parse(values);
    try {
      await upsert.mutateAsync({
        packId: parsed.packId,
        displayName: parsed.displayName,
        ...(parsed.sortOrder !== undefined ? { sortOrder: parsed.sortOrder } : {}),
      });
      toast.success(isEdit ? 'Pack updated' : 'Pack created');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the pack');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit sticker pack' : 'New sticker pack'}</DialogTitle>
          <DialogDescription>
            A pack is one tab in the mobile sticker picker.
            {isEdit ? ' The id is fixed — it is the storage folder for every sticker in it.' : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <label htmlFor="packId" className="text-sm font-medium">
              Pack id
            </label>
            <Input
              id="packId"
              autoComplete="off"
              readOnly={isEdit}
              className={isEdit ? 'text-muted-foreground' : undefined}
              {...register('packId')}
            />
            {errors.packId && <p className="text-xs text-destructive">{errors.packId.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="displayName" className="text-sm font-medium">
              Tab label
            </label>
            <Input id="displayName" autoComplete="off" {...register('displayName')} />
            {errors.displayName && (
              <p className="text-xs text-destructive">{errors.displayName.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="sortOrder" className="text-sm font-medium">
              Tab position
            </label>
            <Input
              id="sortOrder"
              type="number"
              min={0}
              placeholder="Leave empty to append last"
              {...register('sortOrder')}
            />
            {errors.sortOrder && (
              <p className="text-xs text-destructive">{errors.sortOrder.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
